import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Observable, catchError, filter, of, switchMap, tap } from 'rxjs';
import {
    NotesWithMentionsDto,
    TimeOffPolicyNoteType,
    TimeOffResourcePolicyNotesDto,
} from '../../../models/notes.models';
import {
    CreateTimeOffResourcePolicyRequest,
    PRACTICE_AREA_OVERRIDES_FRAGMENT,
    SetCurrentYearAvailableRequest,
    TimeOffResourcePolicyDto,
    TimeOffTypeCodes,
    UpdateTimeOffResourcePolicyRequest,
} from '../../../models/time-off.models';
import { NotesService } from '../../../services/notes.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { TimeOffService } from '../../../services/time-off.service';
import { describeApiError } from '../../../utils/api-error.utils';
import {
    UnsavedChangesDialogComponent,
    UnsavedChangesDialogData,
} from '../unsaved-changes-dialog/unsaved-changes-dialog.component';

export interface TimeOffPolicyEditDialogData {
  employeeId: string;
  employeeName?: string;
  typeCode: string;
  typeName: string;
  typeLookupValueId: number;
  defaultEntitled: number;
  /** Current-year prorated entitlement — the part an adjustment changes (floored at 0). */
  proratedEntitled: number;
  /**
   * Current-year total available (entitled + the carry-over amount the server actually counts
   * under its date-aware rule). The carry-over floor for the current-year total is derived from
   * totalAvailable - proratedEntitled (PBI 6228 removed the standalone carryoverDays field).
   */
  totalAvailable: number;
  /**
   * The inherited entitlement before any resource-level override — the practice-area override
   * when one is in force for this resource, otherwise the office default. Which one it is comes
   * from `practiceAreaCode`.
   */
  officeDefaultEntitledDays: number | null;
  /**
   * The practice area whose override produced `officeDefaultEntitledDays`, or null when the office
   * default did. Drives the banner wording: the number is the same either way, but sending the
   * admin to "change the office default" when a practice-area override is in force is wrong.
   */
  practiceAreaCode: string | null;
  /** Primary office code, used to link to the office details page. */
  officeCode: string | null;
  /**
   * The year the supplied balance was computed for, as resolved by the server from its own (UTC)
   * clock. Drives the displayed years and the save target so the browser's local year can never
   * disagree with the year the server will validate against. Falls back to the browser's UTC year.
   */
  year?: number;
  /** All active policy rows for this type (baseline + effective-year dated rows). */
  typePolicies: TimeOffResourcePolicyDto[];
}

export interface TimeOffPolicyEditDialogResult {
  saved: boolean;
}

@Component({
  selector: 'app-time-off-policy-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './time-off-policy-edit-dialog.component.html',
  styleUrls: ['./time-off-policy-edit-dialog.component.scss'],
})
export class TimeOffPolicyEditDialogComponent implements OnInit {
  private dialogRef =
    inject<
      MatDialogRef<
        TimeOffPolicyEditDialogComponent,
        TimeOffPolicyEditDialogResult | null
      >
    >(MatDialogRef);
  data = inject<TimeOffPolicyEditDialogData>(MAT_DIALOG_DATA);
  private timeOffService = inject(TimeOffService);
  private notesService = inject(NotesService);
  private practiceAreaService = inject(PracticeAreaService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  isLoading = false;
  isSaving = false;

  /**
   * Display name of the practice area whose override is in force, resolved after open. Starts as
   * the code so the banner is never blank while the lookup runs, and stays the code if the lookup
   * fails — the name is decoration, the attribution is the point.
   */
  practiceAreaName: string | null = null;

  // Section 2 — the absolute policy total effective next year onward, edited directly.
  // Nullable: the number input clears to null, which counts as an invalid entry.
  futureTotal: number | null = 0;
  // Section 1 — the absolute total available for the current year, edited directly.
  currentYearTotal: number | null = 0;
  notesInput = '';

  // Prefer the server's year (from the balance it computed) over the browser's: the API validates
  // the requested year against its own UTC clock, and a local clock can be a year ahead or behind
  // it around New Year. Both terms of the staleness guard in save() read the same UTC basis.
  readonly currentYear = this.data.year ?? new Date().getUTCFullYear();
  readonly nextYear = this.currentYear + 1;

  // Which mode the dialog is in — only one section shows at a time.
  mode: 'currentYear' | 'policy' = 'currentYear';

  // Drive each field's error state from our own validity check (rather than the
  // native `required` attribute, which would surface the browser's validation
  // tooltip). Only flag once the user has interacted with the field.
  readonly futureErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean =>
      this.hasInteracted(control) && !this.isFutureValid,
  };
  readonly adjustmentErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean =>
      this.hasInteracted(control) && !this.isAdjustmentValid,
  };

  private originalNotes = '';
  private existingNote: NotesWithMentionsDto | null = null;

  // Existing balances captured at open — the "(currently: X)" figures shown in each label.
  // They double as the dirty baselines: a save writes only when the entered absolute total
  // differs from its baseline. Rebaselined after a successful day-save so a notes-retry
  // doesn't re-issue the (non-idempotent) day write. Public: read by the labels.
  currentYearBaseline = 0;
  futureBaseline = 0;

  get isBereavement(): boolean {
    return this.data.typeCode === TimeOffTypeCodes.BEREAVEMENT;
  }

  /** e.g. "Available vacation days" / "Available sick days". */
  get availableDaysLabel(): string {
    return `Available ${this.data.typeName.toLowerCase()} days`;
  }

  /** Title for the current-year mode button, e.g. "Set Vacation Days". */
  get setDaysTitle(): string {
    return `Set ${this.data.typeName} Days`;
  }

  /** The adjustment endpoint only supports vacation; other types are policy-only. */
  get isAdjustmentSupported(): boolean {
    return this.data.typeCode === TimeOffTypeCodes.VACATION;
  }

  /** True when the inherited figures come from a practice-area override, not the office default. */
  get inheritedFromPracticeArea(): boolean {
    return !!this.data.practiceAreaCode;
  }

  /**
   * Absolute URL to the office details page, opened in a new tab from the info banner so
   * the user's unsaved changes in this dialog are never disturbed. Empty when no office.
   */
  get officeDetailsUrl(): string {
    if (!this.data.officeCode) return '';
    return this.router.serializeUrl(
      this.router.createUrlTree(['/admin/offices', this.data.officeCode]),
    );
  }

  /**
   * Same page, but with the fragment that opens the Practice Area Overrides section and scrolls
   * to it. Used only by the practice-area wording: the office-default banner deliberately points
   * at the office defaults instead, which are a different control further up the same card.
   */
  get practiceAreaOverridesUrl(): string {
    if (!this.data.officeCode) return '';
    return this.router.serializeUrl(
      this.router.createUrlTree(['/admin/offices', this.data.officeCode], {
        fragment: PRACTICE_AREA_OVERRIDES_FRAGMENT,
      }),
    );
  }

  /**
   * The resource policy row in effect for next year — greatest effectiveYear <= nextYear,
   * null effectiveYear as the baseline. Returns null when the resource has no applicable row
   * (the office default applies). Mirrors the API's whole-row resolution.
   */
  private resolvedNextYearPolicy(
    forYear: number = this.nextYear,
  ): TimeOffResourcePolicyDto | null {
    const applicable = this.data.typePolicies
      .filter((p) => p.effectiveYear == null || p.effectiveYear <= forYear)
      .sort(
        (a, b) =>
          (b.effectiveYear ?? Number.MIN_SAFE_INTEGER) -
          (a.effectiveYear ?? Number.MIN_SAFE_INTEGER),
      );
    return applicable[0] ?? null;
  }

  get isFutureValid(): boolean {
    return this.isValidTotal(this.futureTotal);
  }

  get isAdjustmentValid(): boolean {
    // Any whole number of days is allowed. A total below the carried-over days is accepted and the
    // server clamps it (it moves only entitled, floored at 0). We deliberately don't floor on the
    // client at the displayed carry-over: before a counts row exists that figure can be the office
    // ceiling, which would wrongly block a lower total the server would actually accept.
    return this.isValidTotal(this.currentYearTotal);
  }

  /**
   * Sanity ceiling on a day count for a single year, mirroring the API's bound on the set-available
   * request so an obvious typo (2555 for 25) is caught before the round trip.
   */
  readonly maxDays = 366;

  /** A total must be a whole number within one year's days; rejects empty/decimal/out-of-range. */
  private isValidTotal(value: number | null): boolean {
    return (
      value != null &&
      Number.isInteger(value) &&
      value >= 0 &&
      value <= this.maxDays
    );
  }

  private hasInteracted(control: FormControl | null): boolean {
    return !!control && (control.touched || control.dirty);
  }

  /**
   * Validation message for a total-days field, or null when valid. Both fields accept any whole
   * number of 0 or more days; the server enforces the current-year carry-over floor by clamping.
   */
  daysError(value: number | null): string | null {
    if (value == null) return 'A number of days is required.';
    if (!Number.isInteger(value)) {
      return 'Enter a whole number of days.';
    }
    if (value < 0) {
      return 'Enter a whole number of days (0 or more).';
    }
    if (value > this.maxDays) {
      return `Enter ${this.maxDays} days or fewer.`;
    }
    return null;
  }

  /**
   * True when the active mode's number field differs from its baseline. Used to
   * decide whether a view switch needs confirmation — notes are deliberately
   * excluded here because they are shared across both views and carry over.
   */
  get isActiveFieldDirty(): boolean {
    return this.mode === 'currentYear'
      ? this.currentYearTotal !== this.currentYearBaseline
      : this.futureTotal !== this.futureBaseline;
  }

  /** True when the active mode's field (or notes) differs from its baseline. */
  get isDirty(): boolean {
    const notes = this.notesInput?.trim() ?? '';
    const originalNotes = this.originalNotes?.trim() ?? '';
    return this.isActiveFieldDirty || notes !== originalNotes;
  }

  /** Validity of the active mode's field. */
  get isActiveFieldValid(): boolean {
    return this.mode === 'currentYear'
      ? this.isAdjustmentValid
      : this.isFutureValid;
  }

  /** Save is enabled only when dirty, the active field is valid, and not busy. */
  get canSave(): boolean {
    return (
      !this.isSaving && !this.isLoading && this.isDirty && this.isActiveFieldValid
    );
  }

  get dialogTitle(): string {
    const name = this.data.employeeName?.trim();
    const base = `Edit ${this.data.typeName} Policy`;
    return name ? `${base} for ${name}` : base;
  }

  ngOnInit(): void {
    // Each field holds an absolute total the user edits directly, pre-filled with the
    // resource's existing balance so "unchanged" = the current value and a save writes only
    // the difference. The "(currently: X)" labels read from these same baselines.

    // Resolve the next-year policy default once — `data` is immutable for the dialog's lifetime.
    this.futureBaseline = Math.max(
      0,
      this.resolvedNextYearPolicy()?.defaultDays ??
        this.data.officeDefaultEntitledDays ??
        this.data.defaultEntitled,
    );
    this.futureTotal = this.futureBaseline;

    // Current-year available balance, supplied by the server as totalAvailable
    // (entitled + carry-over) — the single source of truth the balance grid also shows.
    this.currentYearBaseline = this.data.totalAvailable;
    this.currentYearTotal = this.currentYearBaseline;

    // Only vacation supports current-year adjustments; other types open straight in
    // policy mode (the adjustment mode card renders disabled).
    if (!this.isAdjustmentSupported) {
      this.mode = 'policy';
    }

    // The parent opens this dialog with disableClose:true. Clicking the backdrop
    // does nothing (the modal only closes via the X button or Save). ESC is
    // routed through close(), which prompts to confirm when the form is dirty.
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.close());

    this.loadNotes();
    this.resolvePracticeAreaName();
  }

  /**
   * Swap the practice-area code for its name in the banner. Failure is swallowed on purpose: the
   * banner already reads correctly with the code, and a lookup fault must not disturb a dialog
   * whose job is editing the resource's policy.
   */
  private resolvePracticeAreaName(): void {
    const code = this.data.practiceAreaCode;
    if (!code) return;

    this.practiceAreaName = code;
    this.practiceAreaService
      .getPracticeAreaByCode(code)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((practiceArea) => {
        this.practiceAreaName = practiceArea?.description || code;
      });
  }

  private loadNotes(): void {
    const noteType = this.mapNoteType();
    if (!noteType || !this.data.employeeId) return;

    this.isLoading = true;
    this.notesService
      .getResourcePolicyNotes(this.data.employeeId)
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (notes: TimeOffResourcePolicyNotesDto | null) => {
          this.existingNote = this.pickNote(notes, noteType);
          this.notesInput = this.existingNote?.notes ?? '';
          this.originalNotes = this.notesInput;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  save(): void {
    if (this.isSaving) return;
    // Guard mirrors the button's [disabled]="!canSave".
    if (!this.canSave) return;

    if (!this.data.employeeId) {
      this.snackbarService.showError('Unable to save: missing data.');
      return;
    }

    // The dialog captured currentYear/nextYear when it opened, and the labels showed those years.
    // If the clock has since rolled into a new year those captured years are stale — a save would
    // target a year the user never saw (and the policy path would silently write a row for the new
    // current year). Compared in UTC, the basis the server validates against, so the two agree
    // across the New Year boundary. Only the day writes carry a year, so this gates on the day
    // field being dirty; a notes-only save targets no year and must still go through.
    if (
      this.isActiveFieldDirty &&
      new Date().getUTCFullYear() !== this.currentYear
    ) {
      this.snackbarService.showError(
        'The year has changed since this dialog was opened. Please close and reopen to save.',
      );
      return;
    }

    this.isSaving = true;

    // Persist the active mode's day change first (adjustment or policy), then the shared
    // notes, then close. On failure the dialog stays open so nothing entered is lost.
    this.saveDaysIfChanged()
      .pipe(switchMap(() => this.saveNotesIfChanged()))
      .subscribe({
        next: () => {
          this.snackbarService.showSuccess('Changes saved');
          this.isSaving = false;
          this.dialogRef.close({ saved: true });
        },
        error: (err) => {
          console.error('Failed to save policy changes:', err);
          this.snackbarService.showError(
            describeApiError(err, 'Failed to save changes.'),
          );
          this.isSaving = false;
        },
      });
  }

  /**
   * Persists the active mode's day value when it changed:
   * - Current-year mode → sets the absolute available total via the set-available endpoint. The
   *   server derives the entitlement from its own current carry-over, so the saved total matches
   *   what the user entered regardless of any stale figure; it floors entitled at 0 and rejects
   *   non-vacation types / non-current years.
   * - Policy mode → the resource policy row effective from `nextYear` onward. The baseline
   *   (un-dated) row is never touched, so the current year's balance is unaffected.
   *
   * Both paths use the years captured when the dialog opened (`currentYear` / `nextYear`) — the
   * same years the labels showed — so a save always targets the year the user was looking at.
   * save() refuses the day write if the clock has rolled into a new year since the dialog opened
   * (a notes-only save carries no year and still goes through), so neither path can silently land
   * on a different year.
   */
  private saveDaysIfChanged(): Observable<unknown> {
    if (!this.isActiveFieldDirty) return of(null);

    if (this.mode === 'currentYear') {
      // Send the absolute total; the server sets the balance to it (no client-side delta).
      const request: SetCurrentYearAvailableRequest = {
        employeeId: this.data.employeeId,
        year: this.currentYear,
        typeLookupValueId: this.data.typeLookupValueId,
        availableDays: this.currentYearTotal ?? 0,
      };
      // Rebaseline from the balance the server returns, not from what was typed: a total below the
      // effective carry-over is clamped server-side (only entitled moves, floored at 0), so the
      // saved figure can be higher than the entered one. Taking the server's value keeps the field
      // and its "(currently: N)" label truthful if a later step fails and the dialog stays open,
      // and makes an unchanged retry (e.g. a notes-only re-save) skip the day write.
      return this.timeOffService.setCurrentYearAvailable(request).pipe(
        tap((balance) => {
          const saved = balance?.balances?.find(
            (b) => b.typeCode === this.data.typeCode,
          )?.totalAvailable;
          this.currentYearBaseline =
            saved ?? this.currentYearTotal ?? this.currentYearBaseline;
          this.currentYearTotal = this.currentYearBaseline;
        }),
      );
    }

    // canSave already guarantees futureTotal is a non-negative integer here.
    const newDefaultDays = this.futureTotal ?? 0;
    // The row in effect for next year (dated row or baseline). Its max-carryover is
    // carried forward so creating a next-year row never zeroes the carryover the
    // resource already had — but only for Vacation, the sole type that keeps a
    // resource-level carryover (Task 6666). The API nulls it for other types anyway.
    const nextYearRow = this.resolvedNextYearPolicy(this.nextYear);
    const isVacation = this.data.typeCode === TimeOffTypeCodes.VACATION;
    // Rebaseline on success so an unchanged retry (e.g. a notes-only re-save) reports no
    // day change and skips the write entirely.
    const markClean = tap(() => {
      this.futureBaseline = this.futureTotal ?? this.futureBaseline;
    });

    if (nextYearRow?.effectiveYear === this.nextYear) {
      // Update the existing next-year row, preserving its max carryover (Vacation only).
      const request: UpdateTimeOffResourcePolicyRequest = {
        employeeId: this.data.employeeId,
        typeLookupValueId: this.data.typeLookupValueId,
        defaultDays: newDefaultDays,
        maxCarryoverDays: isVacation ? nextYearRow.maxCarryoverDays : undefined,
        effectiveYear: this.nextYear,
      };
      return this.timeOffService
        .updateResourcePolicy(nextYearRow.id, request)
        .pipe(markClean);
    }

    // Create a new next-year row, inheriting the max carryover from whatever policy was
    // in effect (baseline or an earlier dated row) for Vacation. For other types, or when
    // none applies (office default only), leave it undefined so the office policy governs.
    const request: CreateTimeOffResourcePolicyRequest = {
      employeeId: this.data.employeeId,
      typeLookupValueId: this.data.typeLookupValueId,
      defaultDays: newDefaultDays,
      maxCarryoverDays: isVacation ? (nextYearRow?.maxCarryoverDays ?? undefined) : undefined,
      effectiveYear: this.nextYear,
    };
    return this.timeOffService.createResourcePolicy(request).pipe(
      // Record the newly-created row so a later save this session — retrying after a
      // notes-save failure, or editing the value again — resolves it and takes the UPDATE
      // branch above, instead of re-issuing a duplicate CREATE the API rejects with a 400.
      tap((created) => {
        this.data.typePolicies = [...this.data.typePolicies, created];
      }),
      markClean,
    );
  }

  /**
   * Close request (X button, backdrop, or ESC). Prompts to confirm when the
   * form has unsaved changes; closes immediately when clean.
   */
  close(): void {
    if (this.isSaving) return;
    if (!this.isDirty) {
      this.forceClose();
      return;
    }
    this.openUnsavedChangesDialog().subscribe((result) => {
      if (result === 'discard') {
        this.forceClose();
      } else if (result === 'save') {
        // save() closes the dialog with { saved: true } once it completes.
        this.save();
      }
      // 'cancel' / undefined — keep the dialog open, nothing to do.
    });
  }

  private forceClose(): void {
    this.dialogRef.close(null);
  }

  /**
   * View-switch request from the mode selector. Switches freely when the active
   * field is unchanged; otherwise prompts to confirm. Notes are shared across
   * views, so a notes-only change does not block a switch.
   */
  requestMode(next: 'currentYear' | 'policy'): void {
    if (this.isSaving || next === this.mode) return;
    if (next === 'currentYear' && !this.isAdjustmentSupported) return;
    if (!this.isActiveFieldDirty) {
      this.mode = next;
      return;
    }
    this.openUnsavedChangesDialog().subscribe((result) => {
      if (result === 'discard') {
        this.resetActiveField();
        this.mode = next;
      } else if (result === 'save') {
        // save() closes the dialog once it completes; the user reopens to edit
        // the other view.
        this.save();
      }
      // 'cancel' / undefined — stay in the current mode (thumb does not move).
    });
  }

  /** Restore the active mode's field to its baseline (used when discarding a switch). */
  private resetActiveField(): void {
    if (this.mode === 'currentYear') {
      this.currentYearTotal = this.currentYearBaseline;
    } else {
      this.futureTotal = this.futureBaseline;
    }
  }

  private openUnsavedChangesDialog(): Observable<'save' | 'discard' | 'cancel'> {
    const data: UnsavedChangesDialogData = {
      entityName: 'Policy',
      // When the active field is invalid, offer "Continue Editing" instead of a
      // "Save Changes" button that would silently no-op against the !canSave guard.
      hasValidationErrors: !this.isActiveFieldValid,
    };
    return this.dialog
      .open(UnsavedChangesDialogComponent, {
        width: '500px',
        disableClose: true,
        data,
      })
      .afterClosed();
  }

  private saveNotesIfChanged(): Observable<unknown> {
    const noteType = this.mapNoteType();
    if (!noteType) return of(null);

    const trimmed = this.notesInput?.trim() ?? '';
    const original = this.originalNotes?.trim() ?? '';

    if (trimmed === original) return of(null);

    if (trimmed.length === 0) {
      return this.notesService.deleteResourcePolicyNote(
        this.data.employeeId,
        noteType,
      );
    }

    return this.notesService.updateResourcePolicyNote(
      this.data.employeeId,
      noteType,
      this.notesInput,
    );
  }

  private mapNoteType(): TimeOffPolicyNoteType | null {
    switch (this.data.typeCode) {
      case TimeOffTypeCodes.VACATION:
        return 'vacation';
      case TimeOffTypeCodes.SICK:
        return 'sick';
      case TimeOffTypeCodes.BEREAVEMENT:
        return 'bereavement';
      default:
        return null;
    }
  }

  private pickNote(
    notes: TimeOffResourcePolicyNotesDto | null,
    noteType: TimeOffPolicyNoteType,
  ): NotesWithMentionsDto | null {
    if (!notes) return null;
    switch (noteType) {
      case 'vacation':
        return notes.vacation;
      case 'sick':
        return notes.sick;
      case 'bereavement':
        return notes.bereavement;
    }
  }
}
